<script setup lang="ts">
import {
  InteractionEventStatus,
  InteractionEventType,
  DispatchStatus,
} from "@/enums/mission";
import { useInteractiveReview } from "@/stores/interactive-review";
import { useMission } from "@/stores/mission";
import { InteractionEvent, MateDispatch } from "@/types/entity";
import { showLoadingToast } from "vant";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useGlobalUser } from "@/stores/globalUser";

const props = defineProps<{
  event: InteractionEvent;
  isNeedAction: boolean;
}>();

const router = useRouter();
const { t } = useI18n();
const reviewStore = useInteractiveReview();
const userStore = useGlobalUser();
const entity = computed<MateDispatch>(
  () => props.event.entity! as MateDispatch
);

const desc = computed<string>(() => {
  if (props.event.type === InteractionEventType.StrollDispatched) {
    return t("interactive.reviewPage.stroll.strollDispatched");
  } else if (props.event.type === InteractionEventType.StrollReturned) {
    return t("interactive.reviewPage.stroll.return");
  } else {
    return t("interactive.reviewPage.stroll.requestStroll");
  }
});
const approveText = computed<string>(() => {
  return t("interactive.reviewPage.stroll.userApproved", {
    name: props.event.mate!.name,
  });
});
const rejectText = computed<string>(() => {
  return t("interactive.reviewPage.stroll.userRejected", {
    name: props.event.mate!.name,
  });
});

const onGoToView = () => {
  if (
    entity.value.status === DispatchStatus.Dispatched ||
    entity.value.status === DispatchStatus.ReturningWithLoots
  ) {
    // 1. 正在派遣： 跳转到missionDetail
    router.push({
      name: "missionDetail",
      query: {
        dispatchUuid: entity.value.uuid,
      },
    });
  } else if (entity.value.status === DispatchStatus.Returned) {
    // 2. 未结算: 跳转到待迎接
    router.push({
      name: "missionHome",
      query: {
        tab: 3,
      },
    });
  } else {
    // 3. 已结算: 跳转手账
    router.push({
      name: "missionJourneyItem",
      query: {
        dispatchUuid: entity.value.uuid,
      },
    });
  }
};

const missionStore = useMission();
const onPickup = async () => {
  const loading = showLoadingToast("");
  try {
    await missionStore.settleMateDispatch(entity.value.id);
    const oldEvent = reviewStore.entities[props.event.id];
    (oldEvent.entity as MateDispatch).status = DispatchStatus.Settled;
    await router.push({
      name: "missionJourneyItem",
      query: {
        dispatchUuid: entity.value.uuid,
      },
    });
  } finally {
    loading.close();
  }
};
</script>
<template>
  <InteractionItemHeader
    :mate="event.mate!"
    :title="event.title"
    :desc="desc"
    icon="iconify ic--baseline-directions-walk"
    :dtCreate="event.dtCreate"
    :is-need-action="isNeedAction"
  />
  <div
    :class="{
      'text-primary-content': isNeedAction,
      'text-base-content': !isNeedAction,
    }"
  >
    {{ event.content }}
  </div>
  <div
    v-if="event.reviewStatus === InteractionEventStatus.Pending"
    class="flex flex-col gap-2"
  >
    <div class="flex gap-2">
      <button
        class="btn btn-xs rounded"
        :class="{
          'text-primary-content border-primary-content bg-transparent':
            isNeedAction,
          'btn-outline': !isNeedAction,
        }"
        @click="
          reviewStore.reviewEvent(event.id, InteractionEventStatus.Approved, 5)
        "
      >
        {{ t("Buttons.agree") }}
        <template v-if="!userStore.isFreeModel()">
          <PowerIcon />
          {{ userStore.getPowerCostByModel(5) }}
        </template>
      </button>
      <button
        class="btn btn-xs rounded"
        :class="{
          'text-primary-content border-primary-content bg-transparent':
            isNeedAction,
          'btn-outline': !isNeedAction,
        }"
        @click="
          reviewStore.reviewEvent(event.id, InteractionEventStatus.Rejected)
        "
      >
        {{ t("Buttons.deny") }}
      </button>
    </div>
    <div v-if="event.duration" class="text-base-content/50">
      {{ t("interactive.reviewPage.expectRunMin", { count: event.duration }) }}
    </div>
  </div>
  <div
    v-if="
      props.event.type === InteractionEventType.StrollReturned &&
      entity?.status === DispatchStatus.Returned
    "
    class="flex flex-col gap-2"
  >
    <button
      class="btn btn-xs rounded w-fit"
      :class="{
        'text-primary-content border-primary-content bg-transparent':
          isNeedAction,
        'btn-outline': !isNeedAction,
      }"
      @click="onPickup"
    >
      {{ t("interactive.reviewPage.stroll.goToSettle") }}
    </button>
  </div>
  <template v-else>
    <div
      v-if="event.reviewStatus === InteractionEventStatus.Approved"
      class="text-success"
    >
      {{ approveText }}
    </div>
    <div
      v-if="event.reviewStatus === InteractionEventStatus.Rejected"
      class="text-error"
    >
      {{ rejectText }}
    </div>
    <button
      v-if="entity"
      class="btn btn-xs rounded w-fit"
      :class="{
        'text-primary-content border-primary-content bg-transparent':
          isNeedAction,
        'btn-outline': !isNeedAction,
      }"
      @click="onGoToView()"
    >
      {{ t("interactive.reviewPage.goToView") }}
    </button>
  </template>
</template>
